/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";
import { useStoreZustandVendasCreditos } from "@/store-zustand/vendas-creditos/credits";

import { handleVendasCteditosPorCliente } from "@/domain/use-cases/vendas-creditos";

export interface IPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export type IVendasCreditos = {
  id: number;
  user_id: number;
  status: number;
  valor: number;
  quantidade_creditos: number;
  data_venda: number;
  tipo_transacao: number;
  observacao: number;
  produto: {
    nome: string;
  };
  designer: {
    nome: string;
  };
  created_at: number;
  updated_at: number;
  deleted_at: string | null;
};
export interface IVendasCreditosResponse {
  code: number;
  status: string;
  message: string;
  data: IVendasCreditos[];
  pagination: IPagination;
}

function mapVendasCreditosToTable(data: IVendasCreditos[]): Record<string, any>[] {
  return data?.map((cliente) => ({
    id: cliente.id,
    nome_produto: cliente?.produto?.nome || "N/A",
    saldo_creditos: cliente?.quantidade_creditos || 0,
    designer: cliente?.designer?.nome || "N/A",
    tipo_transacao: cliente?.tipo_transacao,
    status: cliente.status || "N/A",
    observacao: cliente.observacao ?? "",
    hasMensagem: Boolean(cliente.observacao !== null),
    data: cliente?.data_venda ? new Date(cliente?.data_venda).toLocaleString("pt-BR") : "N/A",
  }));
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

/**
 * Buscar transações de vendas de um cliente
 * @param user_cliente_id
 * @returns
 */
function useVendasCreditosPorCliente(user_cliente_id: number) {
  const { currentPage, itemsPerPage, searchItem, setPaginations, setLastPage } =
    useStoreZustandVendasCreditos();

  const { token: accessToken } = useStoreZustandUserAuth();

  // Chave de cache única baseada na página atual e na quantidade de itens por página
  const queryKey = ["vendas-creditos", currentPage, itemsPerPage, searchItem];

  const {
    isLoading,
    error: isError,
    data: vendasCreditosResponse,
  } = useQuery<IVendasCreditosResponse>({
    queryKey,
    queryFn: async () => {
      const response = await handleVendasCteditosPorCliente(
        accessToken!,
        user_cliente_id,
        currentPage,
        itemsPerPage,
        searchItem!
      );

      return response;
    },
  });

  const vendasCreditos = mapVendasCreditosToTable(vendasCreditosResponse?.data as any);
  const pagination = vendasCreditosResponse?.pagination;

  // ✅ Só recalcula se currentPage ou last_page mudar
  const pages = useMemo(() => {
    return generatePagination(currentPage, pagination?.last_page ?? 1);
  }, [currentPage, pagination?.last_page]);

  // ✅ Atualiza Zustand uma única vez
  useEffect(() => {
    setPaginations(pages as number[]);
    setLastPage(pagination?.last_page as number);
  }, [pages, setPaginations, setLastPage, pagination]);

  return {
    vendasCreditos: vendasCreditos ?? [],
    paginations: pages,
    isLoading,
    isError,
  };
}

/**
 * EXPORTS
 */
export { useVendasCreditosPorCliente };
